import React from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowRight from '@mui/icons-material/ArrowRight';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import FallRiskCharts from '../charts/FallRiskCharts';
import StaticInfographic from './StaticInfographic';

/**
 * Component for rendering comprehensive fall risk infographics with charts and information
 * @param {Object} props - Component props
 * @param {String} props.type - Type of infographic to render
 * @param {String} props.title - Infographic title
 * @param {String} props.description - Infographic description
 * @returns {JSX.Element} - The rendered component
 */
const FallRiskInfographics = ({ type = 'assessmentFactors', title, description }) => {
  // Define the infographic content based on type
  const renderInfographic = () => {
    switch(type) {
      case 'assessmentFactors':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FallRiskCharts 
                chartType="multiBar" 
                title="Fall Risk Assessment Factors" 
                description="The major categories of risk factors that contribute to falls in older adults. Intrinsic factors include age-related changes, while environmental factors include home hazards."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StaticInfographic module="UnderstandingFallRisks" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
                    Key Considerations for Patient-Specific Risk Factors
                  </Typography>
                  <Typography variant="body2" paragraph>
                    When assessing fall risks, it's essential to consider the specific conditions of each patient:
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e57373' }}>
                        Parkinson's Disease:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Monitor for freezing of gait, postural instability, and medication timing effects.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#64b5f6' }}>
                        Arthritis:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Assess joint pain, stiffness, and mobility limitations that may affect balance.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#4db6ac' }}>
                        Dementia:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Consider cognitive status, wandering risk, and ability to recognize hazards.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#9575cd' }}>
                        Diabetes:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Check for peripheral neuropathy, vision issues, and hypoglycemia risk.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#ffb74d' }}>
                        Mobility Limitations:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Evaluate assistive device usage, transfer safety, and environmental adaptations needed.
                      </Typography>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      
      case 'preventionEffectiveness':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FallRiskCharts 
                chartType="bar" 
                title="Fall Prevention Intervention Effectiveness" 
                description="Percentage reduction in fall risk by intervention type, based on clinical research."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StaticInfographic module="CaregiverBestPractices" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
                    Tailoring Interventions to Patient Needs
                  </Typography>
                  <Typography variant="body2" paragraph>
                    The effectiveness of fall prevention strategies varies based on patient-specific factors:
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e57373' }}>
                        Exercise Programs:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Most effective for those with mild to moderate mobility issues. For Parkinson's patients, focus on balance and gait training.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#64b5f6' }}>
                        Home Modifications:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Critical for patients with mobility devices. For arthritis patients, focus on bathroom and kitchen adaptations.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#4db6ac' }}>
                        Medication Review:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Essential for patients on multiple medications. For diabetes patients, monitor for hypoglycemic medications.
                      </Typography>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      
      case 'ageRiskFactors':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FallRiskCharts 
                chartType="line" 
                title="Fall Risk by Age Group" 
                description="The percentage risk of falls increases significantly with age, with the steepest increase after age 80."
              />
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
                    Age-Related Risk Factors
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e57373' }}>
                        65-74 Years:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        • Beginning decline in muscle mass
                        • Early vision and hearing changes
                        • Increased medication use
                        • For arthritis: Joint stiffness becoming more pronounced
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#64b5f6' }}>
                        75-84 Years:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        • Significant decrease in balance
                        • Slower reaction times
                        • Increased cognitive changes
                        • For Parkinson's: More pronounced motor symptoms
                        • For diabetes: Greater risk of neuropathy
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#4db6ac' }}>
                        85+ Years:
                      </Typography>
                      <Typography variant="body2" paragraph>
                        • Substantial muscle weakness
                        • Significant balance impairment
                        • Multiple chronic conditions
                        • For dementia: Higher risk of confusion
                        • For mobility limitations: Greater dependence on assistance
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      
      case 'conditionComparison':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FallRiskCharts 
                chartType="radar" 
                title="Condition-Specific Impact on Fall Risk Factors" 
                description="This radar chart compares how different medical conditions affect various aspects of fall risk."
              />
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
                    Condition-Specific Considerations
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Each medical condition affects fall risk through different mechanisms:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e57373' }}>
                        Parkinson's Disease:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Freezing of gait during transitions" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Postural instability" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Medication timing affects mobility" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#e57373', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Reduced arm swing affecting balance" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#64b5f6' }}>
                        Arthritis:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Joint pain limiting movement" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Morning stiffness increasing risk" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Reduced range of motion" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#64b5f6', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Pain medication side effects" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#4db6ac' }}>
                        Dementia:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Impaired hazard recognition" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Wandering behaviors" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Difficulty following safety instructions" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#4db6ac', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Impaired judgment of capabilities" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#9575cd' }}>
                        Diabetes:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Peripheral neuropathy affecting sensation" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Vision changes from retinopathy" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Hypoglycemia causing dizziness" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Delayed wound healing complicating injuries" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      
      case 'riskFactorBreakdown':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FallRiskCharts 
                chartType="pie" 
                title="Fall Risk Factors Breakdown" 
                description="The relative contribution of different factors to overall fall risk in older adults."
              />
            </Grid>
            <Grid item xs={12}>
              <StaticInfographic module="RiskAssessment" />
            </Grid>
          </Grid>
        );

      case 'communicationStrategies':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FallRiskCharts 
                chartType="radar" 
                title="Communication Impact on Fall Prevention" 
                description="The impact of different communication approaches on reducing fear of falling and improving patient outcomes."
              />
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ bgcolor: 'rgba(121, 85, 72, 0.05)', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#795548', p: 2, color: 'white' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Effective Communication Framework
                  </Typography>
                </Box>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(121, 85, 72, 0.1)', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#795548', mb: 2, textAlign: 'center' }}>
                          LISTEN
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Give full attention" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Maintain eye contact" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Allow time to express concerns" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Note non-verbal cues" />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(121, 85, 72, 0.1)', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#795548', mb: 2, textAlign: 'center' }}>
                          ACKNOWLEDGE
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Validate feelings" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Recognize specific concerns" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Show empathy" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Avoid dismissing fears" />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(121, 85, 72, 0.1)', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#795548', mb: 2, textAlign: 'center' }}>
                          INFORM
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Use clear language" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Explain safety measures" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Provide specific instructions" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Tailor to cognitive level" />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(121, 85, 72, 0.1)', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#795548', mb: 2, textAlign: 'center' }}>
                          EMPOWER
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Focus on capabilities" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Celebrate progress" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Encourage participation" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#795548', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Build confidence" />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'medium', color: '#795548' }}>
                    Patient-Specific Adaptations
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#a1887f' }}>
                        For Parkinson's Disease:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ color: '#a1887f', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Use clear, direct instructions" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ color: '#a1887f', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Allow extra time for responses" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ color: '#a1887f', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Provide rhythmic verbal cues for movement" />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#a1887f' }}>
                        For Dementia:
                      </Typography>
                      <List dense disablePadding>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ color: '#a1887f', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Use simple, one-step instructions" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ color: '#a1887f', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Maintain consistent routines" />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ color: '#a1887f', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText primary="Utilize visual demonstrations" />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
        
      case 'independenceVsSafety':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FallRiskCharts 
                chartType="pie" 
                title="Adaptive Safety Approaches" 
                description="Distribution of adaptive approaches that maintain dignity while ensuring safety."
              />
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(56, 142, 60, 0.15)' }}>
                <Box sx={{ 
                  background: 'linear-gradient(90deg, #388e3c 0%, #66bb6a 100%)', 
                  p: 2.5, 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Adaptive Safety Continuum
                  </Typography>
                </Box>
                <CardContent sx={{ p: 3, bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'white', 
                    borderRadius: 2, 
                    border: '1px dashed rgba(56, 142, 60, 0.3)',
                    mb: 3
                  }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#388e3c', mb: 1 }}>
                      Adaptive Safety: A New Paradigm
                    </Typography>
                    <Typography variant="body2">
                      Rather than viewing independence and safety as opposing forces, adaptive safety integrates both by 
                      modifying environments and approaches to match changing abilities. This preserves dignity while 
                      minimizing risks.
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 2, 
                        bgcolor: 'white',
                        border: '1px solid rgba(56, 142, 60, 0.2)',
                        mb: 3,
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '5px',
                          background: 'linear-gradient(90deg, #f44336 0%, #ffeb3b 50%, #4caf50 100%)'
                        }} />
                        <Typography variant="h6" gutterBottom sx={{ color: '#388e3c', fontWeight: 600, mt: 1 }}>
                          The Adaptive Safety Continuum
                        </Typography>
                        
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                                High Protection
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 1 }}>
                                Maximum safety measures with significant limitations on independence
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(255, 235, 59, 0.1)', borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ color: '#f57f17', fontWeight: 600 }}>
                                Balanced Approach
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 1 }}>
                                Targeted safety interventions that preserve most independence
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ color: '#388e3c', fontWeight: 600 }}>
                                Maximum Independence
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 1 }}>
                                Minimal safety interventions with focus on autonomy
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', bgcolor: 'white', boxShadow: '0 2px 10px rgba(56, 142, 60, 0.1)' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ color: '#388e3c', fontWeight: 600, borderBottom: '2px solid #81c784', pb: 1 }}>
                            Environmental Adaptations
                          </Typography>
                          <List dense>
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Graded Lighting Systems" 
                                secondary="Motion-activated pathway lighting that's unobtrusive"
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Smart Home Integration" 
                                secondary="Voice-activated controls for independence with monitoring"
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Disguised Safety Features" 
                                secondary="Grab bars designed as towel racks, decorative contrast strips"
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Adaptive Equipment Placement" 
                                secondary="Strategic positioning that doesn't dominate living spaces"
                              />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%', bgcolor: 'white', boxShadow: '0 2px 10px rgba(56, 142, 60, 0.1)' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ color: '#388e3c', fontWeight: 600, borderBottom: '2px solid #81c784', pb: 1 }}>
                            Behavioral Adaptations
                          </Typography>
                          <List dense>
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Scheduled Independence" 
                                secondary="Plan higher-risk activities when support is available"
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Graded Assistance" 
                                secondary="Provide only the minimum help needed for task completion"
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Choice Architecture" 
                                secondary="Structure options to naturally promote safer choices"
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Safety Rehearsal" 
                                secondary="Practice emergency responses to build confidence"
                              />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3, p: 2.5, bgcolor: 'white', borderRadius: 2, border: '1px solid rgba(56, 142, 60, 0.2)' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#388e3c', mb: 2 }}>
                      Adaptive Safety Assessment Tool
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: 'rgba(129, 199, 132, 0.1)',
                          height: '100%'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#388e3c', mb: 1 }}>
                            Assessment Questions
                          </Typography>
                          <List dense disablePadding>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText primary="How important is this activity to the person's identity and quality of life?" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText primary="What specific risks are present and how severe are the potential consequences?" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText primary="What is the least restrictive adaptation that would mitigate the identified risks?" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText primary="How can we preserve dignity and autonomy while implementing safety measures?" />
                            </ListItem>
                          </List>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: 'rgba(129, 199, 132, 0.1)',
                          height: '100%'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#388e3c', mb: 1 }}>
                            Implementation Principles
                          </Typography>
                          <List dense disablePadding>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText primary="Start with the least restrictive option and adjust only as needed" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText primary="Integrate safety measures discreetly into the environment and routine" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText primary="Involve the care recipient in designing and implementing adaptations" />
                            </ListItem>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText primary="Regularly reassess as conditions and abilities change" />
                            </ListItem>
                          </List>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      
      case 'fearReductionStrategies':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FallRiskCharts 
                chartType="line" 
                title="Fear Reduction Impact on Mobility" 
                description="How reducing fear of falling improves mobility and independence over time."
              />
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(138, 80, 180, 0.15)' }}>
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #8a50b4 0%, #673ab7 100%)', 
                  p: 2.5, 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Fear Reduction Framework
                  </Typography>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    borderRadius: '50%', 
                    width: 40, 
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <SentimentSatisfiedAltIcon />
                  </Box>
                </Box>
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(103, 58, 183, 0.05)', 
                        borderRadius: 2, 
                        border: '1px dashed rgba(103, 58, 183, 0.3)',
                        mb: 3
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#673ab7', mb: 1 }}>
                          Understanding Fear of Falling
                        </Typography>
                        <Typography variant="body2">
                          Fear of falling creates a cycle that limits mobility and increases fall risk. Breaking this cycle through 
                          targeted interventions and positive reinforcement is essential for maintaining independence and quality of life.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2.5, 
                        height: '100%',
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: 'rgba(103, 58, 183, 0.08)'
                      }}>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          width: 8, 
                          height: '100%', 
                          bgcolor: '#673ab7' 
                        }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#673ab7', mb: 2, ml: 1 }}>
                          Identify
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#673ab7' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Recognize fear behaviors" 
                              secondary="Avoidance, clutching, hesitation"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#673ab7' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Assess fear severity" 
                              secondary="Use validated scales when possible"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#673ab7' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Identify specific triggers" 
                              secondary="Stairs, crowds, uneven surfaces"
                            />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2.5, 
                        height: '100%',
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: 'rgba(103, 58, 183, 0.08)'
                      }}>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          width: 8, 
                          height: '100%', 
                          bgcolor: '#9c27b0' 
                        }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 2, ml: 1 }}>
                          Intervene
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#9c27b0' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Gradual exposure therapy" 
                              secondary="Progressive confidence building"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#9c27b0' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Cognitive restructuring" 
                              secondary="Challenge negative thoughts"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#9c27b0' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Relaxation techniques" 
                              secondary="Deep breathing before activities"
                            />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2.5, 
                        height: '100%',
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: 'rgba(103, 58, 183, 0.08)'
                      }}>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          width: 8, 
                          height: '100%', 
                          bgcolor: '#7e57c2' 
                        }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7e57c2', mb: 2, ml: 1 }}>
                          Reinforce
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#7e57c2' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Celebrate small victories" 
                              secondary="Acknowledge each achievement"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#7e57c2' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Document progress" 
                              secondary="Keep a mobility journal"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: '#7e57c2' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Build social support" 
                              secondary="Involve family in reinforcement"
                            />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(103, 58, 183, 0.05)', borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#673ab7', mb: 1 }}>
                      Condition-Specific Approaches
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#9575cd' }}>
                          For Parkinson's Disease:
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Focus on rhythmic movements with music" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Use visual cues for pathways" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Schedule activities during medication 'on' times" />
                          </ListItem>
                        </List>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#9575cd' }}>
                          For Post-Fall Trauma:
                        </Typography>
                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Revisit fall location with support" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Practice fall recovery techniques" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ArrowRight sx={{ color: '#9575cd', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText primary="Use guided imagery for anxiety reduction" />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      
      default:
        return (
          <Typography color="error">
            Infographic type '{type}' not supported
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ my: 3 }}>
      {title && (
        <Typography variant="h5" gutterBottom sx={{ color: '#2c3e50', fontWeight: 600 }}>
          {title}
        </Typography>
      )}
      
      {description && (
        <Typography variant="body1" sx={{ mb: 3 }}>
          {description}
        </Typography>
      )}
      
      {renderInfographic()}
      
      <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'right', color: 'text.secondary' }}>
        Source: Data compiled from National Council on Aging, CDC STEADI program, and peer-reviewed research
      </Typography>
    </Box>
  );
};

export default FallRiskInfographics;
